"use client";

import { TrendingUp } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";

import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@/components/ui/chart";
import type { ChartConfig } from "@/components/ui/chart";
import type { ChartDataItem } from "../../utils/chart-values";
import { vasChartConfig } from "./utils";

interface VasChartProps {
	chartData: ChartDataItem[];
}

export function VasChart({ chartData }: VasChartProps) {
	// Filter out data points with no entries
	const filteredData = chartData.filter((point) => point.entries_count > 0);

	const chartConfig = {
		"VAS Score": {
			label: "VAS Score",
			color: "var(--chart-1)",
		},
	} satisfies ChartConfig;

	return (
		<ChartContainer config={chartConfig}>
			<AreaChart
				accessibilityLayer
				data={filteredData}
				margin={{
					left: 12,
					right: 12,
				}}
			>
				<CartesianGrid vertical={false} />
				<XAxis
					dataKey="timing"
					tickLine={false}
					axisLine={false}
					tickMargin={8}
				/>
				<YAxis
					domain={[0, 10]}
					tickLine={false}
					axisLine={false}
					tickMargin={8}
					label={{
						value: "VAS Score",
						angle: -90,
						position: "insideLeft",
					}}
				/>
				<ChartTooltip
					cursor={false}
					content={
						<ChartTooltipContent
							hideLabel
							className="p-3 w-[200px] md:w-[300px]"
							formatter={(value, name, item) => (
								<div className="flex flex-col gap-2">
									<div className="flex flex-col">
										<span className="mb-1 text-[0.65rem] text-slate-400 uppercase tracking-wider">
											Timing
										</span>
										<span className="font-medium text-slate-700 text-sm">
											{item.payload.timing}
										</span>
									</div>

									<div className="flex flex-col bg-slate-50 shadow-sm p-2 border border-slate-200 rounded-md">
										<span className="mb-1 font-bold text-[0.65rem] text-slate-500 uppercase tracking-wider">
											Visual Analogue Scale
										</span>
										<span className="flex items-center font-semibold text-slate-900 text-lg">
											<span className="text-indigo-600">{value}/10</span>
											<span className="px-2 text-slate-500 text-sm">=</span>
											<span className="bg-indigo-100 px-2 py-0.5 rounded-full text-slate-600 text-sm">
												{Math.round((Number(value) / 10) * 100)}%
											</span>
										</span>
									</div>
								</div>
							)}
						/>
					}
				/>
				<defs>
					<linearGradient id="fillVas" x1="0" y1="0" x2="0" y2="1">
						<stop offset="5%" stopColor="var(--chart-1)" stopOpacity={0.8} />
						<stop offset="95%" stopColor="var(--chart-1)" stopOpacity={0.1} />
					</linearGradient>
				</defs>
				<Area
					dataKey="value"
					type="natural"
					fill="url(#fillVas)"
					fillOpacity={0.4}
					stroke="var(--chart-1)"
				/>
			</AreaChart>
		</ChartContainer>
	);
}
